import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {AnalysisGraphQL} from 'analysis-tool/AnalysisTypes';
import {SaveToResultsTarget} from 'analysis-tool/structured-data-mapping/targetSpecificColumns';
import {AnalysisTemplateGraphQL} from 'analysis-tool/templates/AnalysisTemplateTypes';
import getDefaultMainViewItemForAnalysis from 'analysis-tool/ui/getDefaultMainViewItemForAnalysis';
import {AnalysisMainViewItemType, MainViewComponentProps} from 'analysis-tool/ui/main-view/MainViewTypes';
import StepConfigurationViewTemplate, {
  StepConfigurationViewTemplateProps,
} from 'analysis-tool/ui/view-templates/StepConfigurationViewTemplate';
import {assert} from 'Asserts';
import FolderSelector from 'file-browser/FolderSelector';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {FONT_PROXIMA_11, FONT_PROXIMA_TITLE, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import CheckboxInput from 'ui-library/inputs/CheckboxInput';
import {StepperV2, stepperStyles} from 'ui-library/stepper/Stepper';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';
import {capitalize} from 'util/NameUtil';
import {getUserMessage} from 'util/Net';

import AnalysisOutputSelector from './AnalysisOutputSelector';
import {
  INITIAL_DOCUMENT_LAYOUT_CONFIGURATION,
  getDocumentSectionLayoutConfig,
  getUpdatedDocumentLayoutConfigurationFromNewSelectedOutputs,
} from './ConfigureDocumentUtil';
import {EXCEEDED_MAX_NUM_OUTPUTS_MESSAGE, MAX_NUM_OUTPUTS} from './Constants';
import createOutputs from './createOutputs';
import EntryAndWorksheetSearchInput, {
  DefaultEntryOrWorksheetInfo,
  getDefaultSelectedEntryOrWorksheetId,
} from './EntryAndWorksheetSearchInput';
import OutputDocumentLayout from './OutputDocumentLayout';
import OutputPreview from './OutputPreview';
import {isSendToDocumentEnabled, isSendToEntryEnabled, isSendToWorksheetEnabled} from './OutputsUtil';
import {
  AnalysisOutput,
  AnalysisOutputType,
  OutputDocumentLayoutConfiguration,
  OutputDocumentLayoutItemType,
  ResultOutputConfig,
} from './Types';

/** @package */
export const RELEASE_VALUE = 'Embedding Analysis Outputs in Notebook Entries';

export type AnalysisCreateOutputsViewProps = MainViewComponentProps<AnalysisMainViewItemType.CREATE_OUTPUTS>;

enum StepperSection {
  OUTPUT_SELECTOR = 'output-selector',
  DATASET_FOLDER_SELECTOR = 'dataset-folder-selector',
  CONFIGURE_DOCUMENT_LAYOUT = 'configure-document-layout',
}

function getDocumentInputLabel(hasTestOrderTableOutputSelected: boolean): string {
  if (hasTestOrderTableOutputSelected) {
    return 'worksheet';
  }
  if (isSendToEntryEnabled() && isSendToWorksheetEnabled()) {
    return 'entry or worksheet';
  } else if (isSendToWorksheetEnabled()) {
    return 'worksheet';
  } else {
    return 'entry';
  }
}

function shouldShowSendToDocumentSectionHeader(numOutputSelections: number): boolean {
  // If we are showing the beta banner or output to worksheet is enabled, then we want this section to appear
  // whether or not sent to entry is enabled. If neither of output to worksheet/entry are enabled, then we
  // hide this section if we aren't showing the beta banner
  return numOutputSelections > 0 && isSendToDocumentEnabled();
}

export default function AnalysisCreateOutputsView({
  analysis,
  clientStateId,
  onCloseStepInitializationView,
}: AnalysisCreateOutputsViewProps): JSX.Element {
  // When adding outputs to a template, `folderId` and `documentId` are not configured.
  const isTemplate = analysis.__typename === 'AnalysisTemplateNode';

  const [selectedOutputs, setSelectedOutputs] = useState<Array<AnalysisOutput>>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    isTemplate ? null : getDefaultSelectedEntryOrWorksheetId(analysis)
  );

  const [documentLayoutConfiguration, _setDocumentLayoutConfiguration] =
    useState<OutputDocumentLayoutConfiguration>(INITIAL_DOCUMENT_LAYOUT_CONFIGURATION);
  const [lockResultTables, setLockResultTables] = useState<boolean>(false);

  function setDocumentLayoutConfiguration(newConfiguration: OutputDocumentLayoutConfiguration): void {
    if (newConfiguration[0].type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE) {
      _setDocumentLayoutConfiguration(newConfiguration);
    } else {
      // Move the first DOCUMENT_SECTION_TITLE to the beginning of the configuration to force all DOCUMENT_CONTENT
      // to be contained within a section even if the user tries to drag the content above the section title
      const firstSectionTitleIndex = newConfiguration.findIndex(
        (item) => item.type === OutputDocumentLayoutItemType.DOCUMENT_SECTION_TITLE
      );
      assert(firstSectionTitleIndex !== -1, 'Did not find any section title.');
      _setDocumentLayoutConfiguration([
        newConfiguration[firstSectionTitleIndex],
        ...newConfiguration.filter((item, idx) => idx !== firstSectionTitleIndex),
      ]);
    }
  }

  const [isFinalizing, setIsFinalizing] = useState(false);

  const isDocumentWorksheet = selectedDocumentId == null ? false : APIUtil.isWorksheet(selectedDocumentId);

  const chartOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.CHART
  );
  const datasetOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.DATASET
  );
  const resultOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.RESULT
  );
  const plateOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.PLATE
  );
  const fileOutputs = selectedOutputs.filter(
    (output) => output.outputConfig.type === AnalysisOutputType.FILE
  );
  const hasTestOrderTableOutputSelected = resultOutputs.some(
    (output) =>
      (output.outputConfig as ResultOutputConfig).targetSpecificMappingState?.target ===
      SaveToResultsTarget.TEST_ORDER_TABLE
  );

  const documentSectionLayout = getDocumentSectionLayoutConfig(
    chartOutputs,
    datasetOutputs,
    resultOutputs,
    documentLayoutConfiguration,
    plateOutputs,
    fileOutputs
  );

  async function onFinalize({
    analysis,
    documentSectionLayout,
    selectedFolderId,
    selectedDocumentId,
    selectedOutputs,
    lockResultTables,
  }: {
    analysis: AnalysisGraphQL | AnalysisTemplateGraphQL;
    documentSectionLayout: PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 1>['sectionLayout'];
    selectedFolderId: string | null;
    selectedDocumentId: string | null;
    selectedOutputs: Array<AnalysisOutput>;
    lockResultTables: boolean;
  }): Promise<void> {
    setIsFinalizing(true);
    try {
      await createOutputs({
        analysis,
        documentSectionLayout,
        selectedFolderId,
        selectedDocumentId,
        selectedOutputs,
        lockResultTables,
      });
      // Surface the backend's user-facing message when present (e.g. a 400 with a specific reason),
      // otherwise fall back to a generic message, and re-enable the button. Don't rethrow — this runs
      // fire-and-forget from onClick, so rethrowing would surface an unhandled rejection. Assertion
      // failures already report to Sentry, but DAO/network failures do not, so log here too.
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (error) {
      logging.error(error);
      notifications.error(
        getUserMessage(error) ??
          'An unexpected error has occurred. Please contact Benchling support if the issue persists.'
      );
      setIsFinalizing(false);
      return;
    }
    onCloseStepInitializationView(getDefaultMainViewItemForAnalysis(analysis));
  }

  const handleCancel: StepConfigurationViewTemplateProps['onCancel'] = (...args) => {
    onCloseStepInitializationView(...args);
  };

  const errors = validateStepConfiguration(
    isTemplate,
    datasetOutputs,
    selectedOutputs,
    selectedFolderId,
    selectedDocumentId,
    documentSectionLayout
  );

  const documentInputLabel = getDocumentInputLabel(hasTestOrderTableOutputSelected);

  return (
    <div
      className={css(styles.templateWrapper)}
      {...getTestProps({component: 'AnalysisCreateOutputsView', element: 'root'})}
    >
      <StepConfigurationViewTemplate
        clientStateId={clientStateId}
        dataFrames={[]}
        disableFinalizeButton={isFinalizing || errors.length > 0}
        disableReason={
          errors.length > 0 ? (
            <span className={css(styles.footerError)}>Resolve all errors to continue</span>
          ) : null
        }
        finalizeButtonText="Create outputs"
        isFinalizing={isFinalizing}
        messages={[]}
        onCancel={handleCancel}
        onFinalize={() =>
          onFinalize({
            analysis,
            documentSectionLayout,
            selectedFolderId,
            selectedDocumentId,
            selectedOutputs,
            lockResultTables,
          })
        }
        renderAdditionalPreviewContent={() => (
          <OutputPreview
            pipeline={analysis}
            clientStateId={clientStateId}
            outputs={selectedOutputs}
            documentLayoutConfiguration={documentLayoutConfiguration}
          />
        )}
        title="Output from analysis"
      >
        <StepperV2
          steps={[
            {
              key: StepperSection.OUTPUT_SELECTOR,
              title: <div className={css(stepperStyles.defaultStepTitle)}>Select items to export</div>,
              content: (
                <div className={classNames(css(stepperStyles.defaultStepContent))}>
                  <AnalysisOutputSelector
                    pipeline={analysis}
                    clientStateId={clientStateId}
                    onUpdateSelectedOutputs={(newSelectedOutputs) => {
                      setSelectedOutputs(newSelectedOutputs);
                      setDocumentLayoutConfiguration(
                        getUpdatedDocumentLayoutConfigurationFromNewSelectedOutputs(
                          documentLayoutConfiguration,
                          selectedOutputs,
                          newSelectedOutputs
                        )
                      );
                      // if selecting a TEST_ORDER_TABLE output and document is not 'workflow', clear the document selection
                      if (
                        newSelectedOutputs.some(
                          (output) =>
                            output.outputConfig.type === AnalysisOutputType.RESULT &&
                            output.outputConfig.targetSpecificMappingState?.target ===
                              SaveToResultsTarget.TEST_ORDER_TABLE
                        ) &&
                        !isDocumentWorksheet
                      ) {
                        setSelectedDocumentId(null);
                      }
                    }}
                    selectedOutputs={selectedOutputs}
                  />
                  {resultOutputs.length > 0 && (
                    <div className={css(styles.checkboxContainer)}>
                      <CheckboxInput
                        id="lock-result-tables"
                        label="Lock result tables"
                        helpText="Prevent edits to result tables. Results in locked tables can still be archived."
                        checked={lockResultTables}
                        onChange={(e) => {
                          setLockResultTables(e.target.checked);
                        }}
                      />
                    </div>
                  )}
                  {errors
                    .filter((e) => e[0] === StepperSection.OUTPUT_SELECTOR)
                    .map((e, i) => (
                      <div key={i} className={css(styles.sectionBanner)}>
                        <InlineBanner bannerType={InlineBannerType.ERROR} text={e[1]} />
                      </div>
                    ))}
                </div>
              ),
            },
            (datasetOutputs.length > 0 || fileOutputs.length > 0) &&
              !isTemplate && {
                key: StepperSection.DATASET_FOLDER_SELECTOR,
                title: (
                  <div className={css(stepperStyles.defaultStepTitle)}>
                    {getFolderStepTitle(datasetOutputs.length, fileOutputs.length)}
                  </div>
                ),
                content: (
                  <div className={classNames(css(stepperStyles.defaultStepContent))}>
                    <div className={css(styles.smallHeader)}>Folder</div>
                    <FolderSelector
                      value={selectedFolderId}
                      onChange={(e: {target: {value: string}}) => {
                        setSelectedFolderId(e.target.value);
                      }}
                      // This prop allows us to select non-root folders
                      showInventory={true}
                    />
                    {errors
                      .filter((e) => e[0] === StepperSection.DATASET_FOLDER_SELECTOR)
                      .map((e, i) => (
                        <div key={i} className={css(styles.sectionBanner)}>
                          <InlineBanner bannerType={InlineBannerType.ERROR} text={e[1]} />
                        </div>
                      ))}
                  </div>
                ),
              },
            shouldShowSendToDocumentSectionHeader(selectedOutputs.length) && {
              key: StepperSection.CONFIGURE_DOCUMENT_LAYOUT,
              title: (
                <div className={css(stepperStyles.defaultStepTitle)}>
                  Select & configure {documentInputLabel}
                </div>
              ),
              content: (
                <>
                  {isSendToDocumentEnabled() && (
                    <div
                      className={classNames(
                        css(styles.configureDocumentSection),
                        css(stepperStyles.defaultStepContent)
                      )}
                    >
                      {!isTemplate && (
                        <>
                          <div>
                            <div className={css(styles.smallHeader)}>
                              {capitalize(documentInputLabel)}{' '}
                              <DefaultEntryOrWorksheetInfo analysis={analysis} />
                            </div>
                            <EntryAndWorksheetSearchInput
                              autoFocus={false}
                              enableSearchEntries={isSendToEntryEnabled() && !hasTestOrderTableOutputSelected}
                              enableSearchWorksheets={isSendToWorksheetEnabled()}
                              isMulti={false}
                              onRemove={() => {
                                setSelectedDocumentId(null);
                              }}
                              onSelect={(documentId) => {
                                setSelectedDocumentId(documentId);
                              }}
                              selected={selectedDocumentId != null ? [selectedDocumentId] : []}
                            />
                          </div>
                          <div className={css(styles.helpText)}>
                            These items will be added to a new step in the selected {documentInputLabel}.
                          </div>
                        </>
                      )}

                      <div>
                        <OutputDocumentLayout
                          documentLayoutConfiguration={documentLayoutConfiguration}
                          onSetDocumentLayoutConfiguration={setDocumentLayoutConfiguration}
                        />
                      </div>
                      {errors
                        .filter((e) => e[0] === StepperSection.CONFIGURE_DOCUMENT_LAYOUT)
                        .map((e, i) => (
                          <div key={i} className={css(styles.sectionBanner)}>
                            <InlineBanner bannerType={InlineBannerType.ERROR} text={e[1]} />
                          </div>
                        ))}
                    </div>
                  )}
                </>
              ),
            },
          ]}
        />
      </StepConfigurationViewTemplate>
    </div>
  );
}

function getFolderStepTitle(datasetCount: number, fileCount: number): string {
  if (datasetCount > 0 && fileCount > 0) {
    return 'Select destination folder';
  }
  if (fileCount > 0) {
    return 'Select file destination';
  }
  return 'Select dataset destination';
}

function validateStepConfiguration(
  isTemplate: boolean,
  datasetOutputs: Array<AnalysisOutput>,
  selectedOutputs: Array<AnalysisOutput>,
  selectedFolderId: string | null,
  selectedDocumentId: string | null,
  documentSectionLayout: PipelineStepConfig<PipelineStepType.SEND_TO_DOCUMENT, 1>['sectionLayout']
): Array<[string | null, string]> {
  const errors: Array<[string, string]> = [];
  const exportsToFolder =
    datasetOutputs.length > 0 ||
    selectedOutputs.some((output) => output.outputConfig.type === AnalysisOutputType.FILE);
  if (selectedOutputs.length === 0) {
    errors.push([StepperSection.OUTPUT_SELECTOR, 'Please select at least one item to export']);
  } else if (selectedOutputs.length > MAX_NUM_OUTPUTS) {
    errors.push([StepperSection.OUTPUT_SELECTOR, EXCEEDED_MAX_NUM_OUTPUTS_MESSAGE]);
  }
  if (exportsToFolder && selectedFolderId == null && !isTemplate) {
    errors.push([StepperSection.DATASET_FOLDER_SELECTOR, 'Select an output folder']);
  }
  if (isSendToDocumentEnabled() && selectedDocumentId == null && !isTemplate) {
    errors.push([StepperSection.CONFIGURE_DOCUMENT_LAYOUT, 'Select an output document']);
  }
  if (documentSectionLayout.some((section) => section.inputReferences.length === 0)) {
    errors.push([StepperSection.CONFIGURE_DOCUMENT_LAYOUT, 'Remove or fill unused sections to continue']);
  }

  return errors;
}

const styles = StyleSheet.create({
  configureDocumentSection: {display: 'flex', flexDirection: 'column', rowGap: 16},
  sectionBanner: {marginTop: '10px'},
  smallHeader: FONT_PROXIMA_TITLE,
  templateWrapper: {height: '100%'},
  helpText: {...FONT_PROXIMA_11, fontStyle: 'italic'},
  footerError: {color: Color.RED_400, fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD},
  checkboxContainer: {marginTop: 16, marginBottom: 8},
});
